import React, { useContext, useState } from 'react';
import { AuthContext } from 'App';
import * as api from 'apis/FusionAPI';
import Cookies from 'js-cookie';
import NativeSignIn from './NativeSignIn';
import LoginSelector from './LoginSelector';
import NativeSignUp from './NativeSignUp';
import FacebookSignin from './FacebookSignin';
import GoogleSignIn from 'components/GoogleSignIn';
import AccountActivation from './AccountActivation';

export default function LoginMenu({ callback, setError }) {
  const auth = useContext(AuthContext);

  const [loginMode, _setLoginMode] = useState('');
  const setLoginMode = (newMode) => {
    _setLoginMode(newMode);
    setError(''); //clear error when mode changes
  };

  const [initSignupData, setInitSignupData] = useState({});
  const [activationData, setActivationData] = useState({});
  const [providerSignup, setProviderSignup] = useState(null);

  const handleLogin = async (formData) => {
    try {
      const response = await api.login(formData.email, formData.password);

      console.log('Reponse from login:', response);

      //TODO: Change this to handle 400 on no account found
      if (response.status !== 200) {
        console.error('Error logging in:', response);
        setError(
          'Error logging in. Please check your email and password and try again.' 
        );
        return;
      }

      if (response?.data?.noEmail) {
        setError(
          'Email does not exist. Try another email or create an account.'
        );
        return;
      }

      //if the account exists but hasn't been activated, go to the activation flow
      if (response?.data?.activationNeeded) {
        sendActivationCode();
        setLoginMode('native-code');
        setActivationData({ email: formData.email });
        return;
      }

      // Log the token to the console
      // console.log('Received data:', response.data);

      Cookies.set('accessToken', response.data.accessToken);
      Cookies.set('refreshToken', response.data.refreshToken);
      Cookies.set('email', response.data.email);
      auth.setAuth({
        ...auth,
        email: response.data.email,
        accessToken: response.data.accessToken,
        refreshToken: response.data.refreshToken,
      });
      await callback({ success: true, message: 'Successfully logged in' });
    } catch (error) {
      console.error('Error logging in:', error);
      setError(error?.response?.data?.message || 'Something went wrong.');
      if (error?.status === 401 && error?.response?.data?.activationNeeded) {
        sendActivationCode();
        setLoginMode('native-code');
        setActivationData({ email: formData.email });
      }
    }
  };

  const handleLoginWithProviderData = async (providerData) => {
    try {
      const res = await api.login(providerData.email, null, providerData);
      console.log('Response from server:', res);

      //TODO: Change this to handle 400 on no account found
      if (res?.status === 200) {
        if (res?.data?.noEmail) {
          //create account flow
          console.log('Need to create account');
          setInitSignupData({
            email: providerData?.email || '',
            name: providerData?.name || '',
          });
          setLoginMode('native-signup');
          setError('Email does not exist. Try creating an account.');
          setProviderSignup(providerData);
          return;
        } else {
          console.log('User successfully logged in');
          Cookies.set('accessToken', res?.data.accessToken);
          Cookies.set('refreshToken', res?.data.refreshToken);
          Cookies.set('email', providerData.email);
          auth.setAuth({
            ...auth,
            email: providerData.email,
            accessToken: res?.data.accessToken,
            refreshToken: res?.data.refreshToken,
          });

          callback({ success: true, message: 'Successfully logged in' });
        }
      } else {
        console.log('Something went wrong');
      }
    } catch (error) {
      if (error.status === 401) setError(<>{error.response.data.message}</>);

      console.log('Error in handleLoginWithProviderData:', error);
    }
  };

  const handleCreateAccount = async (formData) => {
    if (formData.password !== formData.verifyPassword) {
      setError('Passwords must match.');
      return;
    }

    if (
      !formData.name ||
      !formData.ageVerified ||
      !formData.email ||
      !formData.password ||
      !formData.gdprConsent
    ) {
      setError('Please fill out all fields.');
      return;
    }

    try {
      // POST request to /signup (i.e. https://devpuapi.overwatch.ai/users/signup)
      const response = await api.signup(
        formData.email,
        formData.password,
        formData.name,
        formData.ageVerified ? '1900-01-01' : null,
        formData.gdprConsent,
        providerSignup
      );

      console.log('Response from signup:', response);

      if (response.status !== 200) {
        console.error('Error signing up:', response);
        return;
      }

      if (!providerSignup) {
        setActivationData({ email: formData.email });
        setLoginMode('native-code');
      } else {
        Cookies.set('accessToken', response.data.accessToken);
        Cookies.set('refreshToken', response.data.refreshToken);
        Cookies.set('email', formData.email);
        auth.setAuth({
          ...auth,
          email: formData.email,
          accessToken: response.data.accessToken,
          refreshToken: response.data.refreshToken,
        });

        callback({ success: true, message: 'Account successfully created.' });
      }

      // Optionally: Redirect to another page here (e.g., dashboard)
    } catch (err) {
      console.error('Error signing up:', err);
      setError(err.message);
    }
  };

  const nativeSignUpCancel = (response) => {
    console.log('Native Sign Up cancel:', response);
    setLoginMode('');
  };

  const facebookCallback = async (response) => {
    try {
      setLoginMode('');

      if (response?.status === 'unknown') {
        console.log('Something went wrong with facebook login.');
        return;
      }

      const providerData = {
        email: response.email,
        name: response.name,
        usersName: response.name.replaceAll(' ', ''),
        providerId: response.id,
        accessToken: response.accessToken,
        scope: null,
        providerName: 'facebook',
        avatarThumb: response.picture.data.url,
        authenticated: true,
      };

      handleLoginWithProviderData(providerData);
    } catch (error) {
      console.log('Error in facebookCallback:', error);
    }
  };

  const facebookCancel = (response) => {
    console.log('Facebook cancel:', response);
    setLoginMode('');
  };

  const googleCallback = async (response) => {
    try {
      setLoginMode('');

      const providerData = {
        email: response.email,
        name: response.name,
        usersName: `${response.given_name}${response.family_name}`,
        providerId: null,
        accessToken: response.access_token,
        providerName: 'google',
        avatarThumb: response.picture,
        authenticated: true,
      };

      handleLoginWithProviderData(providerData);
    } catch (error) {
      console.log('Error in googleCallback:', error);
    }
  };

  const googleCancel = (response) => {
    console.log('Google cancel:', response);
    setLoginMode('');
  };

  const activateAccountCallback = async (formData) => {
    console.trace('activateAccountCallback:', formData);

    if (!activationData.email) {
      console.log(
        'Something went wrong when sending activation code. Email missing.'
      );
      setLoginMode('');
      setError('No email entered.');
      return;
    }

    if (!formData.code) {
      setError('You must enter a code to activate your account.');
      return;
    }

    try {
      const response = await api.activateAccount(
        activationData.email,
        formData.code
      );

      if (response?.status === 200 && response?.data?.success) {
        Cookies.set('accessToken', response.data.accessToken);
        Cookies.set('refreshToken', response.data.refreshToken);
        Cookies.set('email', activationData.email);
        auth.setAuth({
          ...auth,
          email: activationData.email,
          accessToken: response.data.accessToken,
          refreshToken: response.data.refreshToken,
        });

        callback({ success: true, message: 'Successfully logged in' });
      } else {
        setError(response?.data?.message || 'Something went wrong.');
      }
    } catch (error) {
      console.log('Error in sendActivationCode:', error);
      setError(error?.response?.data?.message || 'Something went wrong.');
    }
  };

  const activateAccountCancel = (response) => {
    console.log('Activate Account cancel:', response);
    setLoginMode('');
  };

  const sendActivationCode = async () => {
    if (!activationData.email) {
      console.log(
        'Something went wrong when sending activation code. Email missing.'
      );
      setLoginMode('');
      return;
    }

    try {
      //resend activation code api
      const response = await api.sendActivationCode(activationData.email);

      console.log('Response from send activation code:', response);
    } catch (error) {
      console.log('Error in sendActivationCode:', error);
    }
  };

  switch (loginMode) {
    case 'native-signup':
      return (
        <NativeSignUp
          initialState={initSignupData}
          callback={handleCreateAccount}
          cancel={nativeSignUpCancel}
        />
      );
    case 'native-code':
      return (
        <AccountActivation
          callback={activateAccountCallback}
          onResend={sendActivationCode}
          cancel={activateAccountCancel}
        />
      );
    case 'native':
      return (
        <NativeSignIn
          onSignUp={() => setLoginMode('native-signup')}
          callback={handleLogin}
          cancel={() => {}}
        />
      );
    case 'facebook':
      return (
        <FacebookSignin callback={facebookCallback} cancel={facebookCancel} />
      );
    case 'google':
      return <GoogleSignIn callback={googleCallback} cancel={googleCancel} />;
    default:
      return <LoginSelector onMethodSelect={setLoginMode} />;
  }
}
